import type { CongaTestEnvelope, ListCongaTestBatchesResponse } from 'types';

import { api } from './api';

/** `CongaTestEnvelope` as it arrives over JSON: ObjectId serializes to a string. */
export type CongaTestRow = Omit<CongaTestEnvelope, 'id'> & { id: string };

export type CongaTestBatchStreamEvent =
  | { type: 'batch.snapshot'; rows: CongaTestRow[] }
  | { type: 'row.updated'; row: CongaTestRow }
  | { type: 'batch.finished' }
  | { type: 'batch.error'; message: string };

export interface CreateCongaTestBatchResponse {
  batchId: string;
  rowCount: number;
}

export interface GetCongaTestBatchResponse {
  batchId: string;
  rows: CongaTestRow[];
}

export interface CongaTestWebhookLog {
  id: string;
  event: string;
  inboundEvent?: string;
  receivedAt?: string;
  /** The raw body Conga sent. */
  data: unknown;
}

export interface GetCongaTestRowWebhookLogsResponse {
  logs: CongaTestWebhookLog[];
}

/**
 * Batch events are delivered over SSE (see `useCongaBatchStream`), so they are not modeled here.
 */
export const congaTestApi = api.injectEndpoints({
  endpoints: (build) => ({
    createCongaTestBatch: build.mutation<CreateCongaTestBatchResponse, FormData>({
      query: (body) => ({
        url: '/internal/conga-test-envelopes/batches',
        method: 'POST',
        body,
      }),
    }),
    listCongaTestBatches: build.query<ListCongaTestBatchesResponse, void>({
      query: () => ({ url: '/internal/conga-test-envelopes/batches' }),
    }),
    getCongaTestBatch: build.query<GetCongaTestBatchResponse, string>({
      query: (batchId) => ({ url: `/internal/conga-test-envelopes/batches/${batchId}` }),
    }),
    getCongaTestRowWebhookLogs: build.query<GetCongaTestRowWebhookLogsResponse, { batchId: string; rowId: string }>({
      query: ({ batchId, rowId }) => ({
        url: `/internal/conga-test-envelopes/batches/${batchId}/rows/${rowId}/webhook-logs`,
      }),
    }),
  }),
});

export const {
  useCreateCongaTestBatchMutation,
  useListCongaTestBatchesQuery,
  useGetCongaTestBatchQuery,
  useGetCongaTestRowWebhookLogsQuery,
} = congaTestApi;
